import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useAppContext } from 'app/useAppContext'
import { AppSettingsModalDataActions } from 'features/AppSettings/Data/AppSettingsModalDataActions'
import { AppSettingsModalDataFields } from 'features/AppSettings/Data/AppSettingsModalDataFields'
import AppSettingsModalDataImport from 'features/AppSettings/Data/AppSettingsModalDataImport'
import { AppSettingsModalDataLayouts } from 'features/AppSettings/Data/AppSettingsModalDataLayouts/AppSettingsModalDataLayouts'
import { AppSettingsModalDataPermissions } from 'features/AppSettings/Data/AppSettingsModalDataPermissions'
import { AppSettingsModalDataSettings } from 'features/AppSettings/Data/AppSettingsModalDataSettings'

import SimpleModal from 'v2/ui/components/SimpleModal'

type ModalData = { title: string; contents: React.ReactNode; width?: string }

type UseToolbarActionsProps = {
    object: ObjectDto
    onModalToggled?: (isOpen: boolean) => void
}

export const useToolbarActions = ({ object, onModalToggled }: UseToolbarActionsProps) => {
    const [modalContents, setModalContents] = useState<ModalData | undefined>()
    const { selectedStack } = useAppContext()

    const isOpen = !!modalContents

    // Add event listener for closing the modal
    useEffect(() => {
        const handleCloseModal = (event: CustomEvent<{ fieldLabel?: string }>) => {
            setModalContents(undefined)
            onModalToggled?.(false)

            // If we have a field label, trigger the search
            if (event.detail?.fieldLabel) {
                // Use requestAnimationFrame to ensure the modal is closed before showing search
                requestAnimationFrame(() => {
                    const searchEvent = new CustomEvent('show-field-search', {
                        detail: { searchValue: event.detail.fieldLabel },
                    })
                    window.dispatchEvent(searchEvent)
                })
            }
        }

        window.addEventListener('close-modal', handleCloseModal as EventListener)
        return () => window.removeEventListener('close-modal', handleCloseModal as EventListener)
    }, [onModalToggled])

    const componentProps = useMemo(
        () => ({
            objectId: object?._sid,
            object,
            stack: selectedStack,
            stackId: selectedStack?._sid,
        }),
        [object, selectedStack]
    )

    const showSettings = useCallback(() => {
        setModalContents({
            title: 'Settings',
            contents: <AppSettingsModalDataSettings noPadding {...componentProps} />,
        })
        onModalToggled?.(true)
    }, [componentProps, onModalToggled])

    const showFields = useCallback(() => {
        setModalContents({
            title: 'Manage Fields',
            contents: <AppSettingsModalDataFields noPadding {...componentProps} />,
        })
        onModalToggled?.(true)
    }, [componentProps, onModalToggled])

    const showPermissions = useCallback(() => {
        setModalContents({
            title: 'Permissions',
            contents: <AppSettingsModalDataPermissions noPadding {...componentProps} />,
        })
        onModalToggled?.(true)
    }, [componentProps, onModalToggled])

    const showActions = useCallback(() => {
        setModalContents({
            title: 'Actions',
            contents: <AppSettingsModalDataActions noPadding {...componentProps} />,
            width: '600px',
        })
        onModalToggled?.(true)
    }, [componentProps, onModalToggled])

    const showLayouts = useCallback(() => {
        setModalContents({
            title: 'Layouts',
            contents: (
                <AppSettingsModalDataLayouts
                    noPadding
                    {...componentProps}
                    onCloseSettingsModal={() => {
                        setModalContents(undefined)
                    }}
                />
            ),
        })
        onModalToggled?.(true)
    }, [componentProps, onModalToggled])

    const showImportData = useCallback(() => {
        setModalContents({
            title: 'Import from CSV',
            contents: <AppSettingsModalDataImport {...componentProps} />,
        })
        onModalToggled?.(true)
    }, [componentProps, onModalToggled])

    const onClose = useCallback(async () => {
        setModalContents(undefined)
        onModalToggled?.(false)
    }, [onModalToggled])

    const toolbarModals = useMemo(
        () => (
            <>
                <SimpleModal
                    title={modalContents?.title}
                    onClose={onClose}
                    open={isOpen}
                    showCloseButton
                    width={modalContents?.width || '800px'}
                    height="500"
                    style={{ maxHeight: '90%' }}
                >
                    {modalContents?.contents}
                </SimpleModal>
            </>
        ),
        [isOpen, modalContents?.contents, modalContents?.title, modalContents?.width, onClose]
    )

    return useMemo(
        () => ({
            toolbarModals,
            showSettings,
            showFields,
            showPermissions,
            showActions,
            showLayouts,
            showImportData,
            closeModal: onClose,
        }),
        [
            toolbarModals,
            showSettings,
            showFields,
            showPermissions,
            showActions,
            showLayouts,
            showImportData,
            onClose,
        ]
    )
}
